import type { MutableRefObject } from "react";
import {
  createNativeSpeechRecognizer,
  isDesktopLocalSpeechRecognitionAvailable,
  isNativeSpeechRecognitionSupported,
  type NativeSpeechRecognizer,
} from "../lib/voice-browser-runtime";
import type { MicPermissionState, VoiceRuntimeState } from "../types/voice";
import { playVoiceReadySuccessSound } from "../utils/soundAlerts";
import {
  VAD_CHECK_INTERVAL_MS,
  VAD_MAX_RECORDING_MS,
  VAD_SILENCE_DURATION_MS,
  VAD_SILENCE_THRESHOLD,
  createAudioContext,
  createRecordingMediaRecorder,
  dispatchUserVoiceVolume,
  getAudioStream,
  userVolumeFromRms,
} from "./voice-audio-session-helpers";

export interface RecordingSession {
  mediaRecorder: MediaRecorder | null;
  speechRecognizer: NativeSpeechRecognizer | null;
  stream: MediaStream;
  audioContext: AudioContext;
  analyser: AnalyserNode;
  vadInterval: ReturnType<typeof setInterval>;
  maxTimer: ReturnType<typeof setTimeout>;
}

export interface StopRecordingOptions {
  process?: boolean;
  releaseInput?: boolean;
}

interface VoiceRecordingRefs {
  recordingRef: MutableRefObject<RecordingSession | null>;
  micReleaseRef: MutableRefObject<(() => void) | null>;
  micStreamRef: MutableRefObject<MediaStream | null>;
  micRequestIdRef: MutableRefObject<number>;
}

export interface VoiceListeningSessionDependencies {
  getStream?: () => Promise<MediaStream>;
  createContext?: () => AudioContext;
  isNativeSpeechSupported?: () => boolean;
  isDesktopLocalSpeechAvailable?: () => boolean;
  createSpeechRecognizer?: typeof createNativeSpeechRecognizer;
  schedule?: (callback: () => void, delayMs: number) => void;
}

interface StartListeningSessionInput extends VoiceRecordingRefs {
  enabledRef: MutableRefObject<boolean>;
  busyRef: MutableRefObject<boolean>;
  isSupported: boolean;
  permission: MicPermissionState;
  soundEnabled: boolean;
  requestMic: (
    id: string,
    priority: number,
  ) => { acquired: boolean; release: () => void };
  stopRecording: (options?: StopRecordingOptions) => void;
  processRecording: (chunks: Blob[]) => void;
  handleNativeTranscript: (transcript: string) => void;
  startListening: () => void;
  setRuntimeState: (state: VoiceRuntimeState) => void;
  setErrorState: () => void;
  onReady: () => void;
}

function isMissingAudioInputError(err: unknown): boolean {
  const name =
    typeof err === "object" && err !== null && "name" in err
      ? String((err as { name?: unknown }).name ?? "")
      : "";
  if (name === "NotFoundError" || name === "DevicesNotFoundError") {
    return true;
  }

  const message = err instanceof Error ? err.message : String(err ?? "");
  return /requested device not found|device not found|no audio input device/i.test(
    message,
  );
}

export function stopVoiceRecordingSession(
  {
    recordingRef,
    micReleaseRef,
    micStreamRef,
    micRequestIdRef,
  }: VoiceRecordingRefs,
  options: StopRecordingOptions = {},
): void {
  const session = recordingRef.current;
  const shouldProcess = options.process !== false;
  if (session) {
    clearInterval(session.vadInterval);
    clearTimeout(session.maxTimer);

    if (session.speechRecognizer) {
      if (shouldProcess) session.speechRecognizer.stop();
      else session.speechRecognizer.abort();
    }

    try {
      if (!shouldProcess && session.mediaRecorder) {
        session.mediaRecorder.onstop = null;
      }
      if (session.mediaRecorder && session.mediaRecorder.state !== "inactive") {
        session.mediaRecorder.stop();
      }
    } catch {
      // Already stopped.
    }

    try {
      session.audioContext.close();
    } catch {
      // Ignore AudioContext shutdown failures.
    }
    recordingRef.current = null;
  }
  dispatchUserVoiceVolume(0, false);

  if (options.releaseInput) {
    micRequestIdRef.current += 1;
    const stream = session?.stream ?? micStreamRef.current;
    stream?.getTracks().forEach((track) => track.stop());
    micStreamRef.current = null;
    if (micReleaseRef.current) {
      micReleaseRef.current();
      micReleaseRef.current = null;
    }
  }
}

export function startVoiceListeningSession(
  {
    enabledRef,
    busyRef,
    recordingRef,
    micReleaseRef,
    micStreamRef,
    micRequestIdRef,
    isSupported,
    permission,
    soundEnabled,
    requestMic,
    stopRecording,
    processRecording,
    handleNativeTranscript,
    startListening,
    setRuntimeState,
    setErrorState,
    onReady,
  }: StartListeningSessionInput,
  dependencies: VoiceListeningSessionDependencies = {},
): void {
  if (
    !enabledRef.current ||
    !isSupported ||
    recordingRef.current ||
    busyRef.current
  ) {
    return;
  }

  if (permission === "denied") {
    setErrorState();
    console.warn("[VoiceAssistant] Microphone permission denied");
    return;
  }

  const existingStream = micStreamRef.current;
  const hasLiveInput = Boolean(
    existingStream
      ?.getAudioTracks()
      .some((track) => track.readyState === "live"),
  );
  if (!existingStream && micReleaseRef.current) return;
  if (existingStream && !hasLiveInput && micReleaseRef.current) {
    micReleaseRef.current();
    micReleaseRef.current = null;
  }
  if (!micReleaseRef.current) {
    const { acquired, release } = requestMic("voice-assistant", 10);
    if (!acquired) {
      console.warn("[VoiceAssistant] Mic arbitration denied");
      return;
    }
    micReleaseRef.current = release;
  }

  const requestId = hasLiveInput ? null : micRequestIdRef.current + 1;
  if (requestId !== null) micRequestIdRef.current = requestId;
  const inputStream = hasLiveInput
    ? Promise.resolve(existingStream!)
    : (dependencies.getStream ?? getAudioStream)();
  inputStream
    .then((stream) => {
      if (requestId !== null && requestId !== micRequestIdRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      micStreamRef.current = stream;
      if (!enabledRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        micStreamRef.current = null;
        if (micReleaseRef.current) {
          micReleaseRef.current();
          micReleaseRef.current = null;
        }
        return;
      }

      setRuntimeState("listening");
      const audioContext = (dependencies.createContext ?? createAudioContext)();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);

      const dataArray = new Float32Array(analyser.fftSize);
      let silenceStart: number | null = null;
      let hasSpeech = false;
      let hasNativeTranscript = false;
      let isRestartingRecognizer = false;
      let hasTerminalRecognizerError = false;
      let ownedSession: RecordingSession | null = null;
      const speechRecognizer =
        (
          dependencies.isNativeSpeechSupported ??
          isNativeSpeechRecognitionSupported
        )() &&
        !(
          dependencies.isDesktopLocalSpeechAvailable ??
          isDesktopLocalSpeechRecognitionAvailable
        )()
          ? (
              dependencies.createSpeechRecognizer ??
              createNativeSpeechRecognizer
            )({
              onTranscript: (transcript) => {
                hasNativeTranscript = true;
                handleNativeTranscript(transcript);
              },
              onEnd: () => {
                if (
                  hasNativeTranscript ||
                  isRestartingRecognizer ||
                  hasTerminalRecognizerError
                ) {
                  return;
                }
                if (
                  !enabledRef.current ||
                  busyRef.current ||
                  !ownedSession ||
                  recordingRef.current !== ownedSession
                ) {
                  return;
                }
                isRestartingRecognizer = true;
                (
                  dependencies.schedule ??
                  ((callback, delayMs) => {
                    window.setTimeout(callback, delayMs);
                  })
                )(() => {
                  isRestartingRecognizer = false;
                  if (
                    enabledRef.current &&
                    ownedSession &&
                    recordingRef.current === ownedSession &&
                    !busyRef.current &&
                    !speechRecognizer?.start()
                  ) {
                    stopRecording({ process: false, releaseInput: true });
                    setErrorState();
                  }
                }, 220);
              },
              onError: (message) => {
                console.warn("[VoiceAssistant] Native speech failed:", message);
                if (!isTerminalSpeechRecognitionError(message)) return;
                hasTerminalRecognizerError = true;
                if (ownedSession && recordingRef.current === ownedSession) {
                  stopRecording({
                    process: false,
                    releaseInput: true,
                  });
                }
                setErrorState();
              },
            })
          : null;

      const mediaRecorder = speechRecognizer
        ? null
        : createRecordingMediaRecorder(stream);
      const chunks: Blob[] = [];

      if (mediaRecorder) {
        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) chunks.push(event.data);
        };
        mediaRecorder.onstop = () => processRecording(chunks);
        mediaRecorder.start(250);
      }

      const vadInterval = setInterval(() => {
        analyser.getFloatTimeDomainData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i += 1) {
          sum += dataArray[i] * dataArray[i];
        }
        const rms = Math.sqrt(sum / dataArray.length);
        dispatchUserVoiceVolume(
          userVolumeFromRms(rms),
          rms > VAD_SILENCE_THRESHOLD,
        );

        if (rms > VAD_SILENCE_THRESHOLD) {
          hasSpeech = true;
          silenceStart = null;
        } else if (!speechRecognizer && hasSpeech) {
          if (!silenceStart) {
            silenceStart = Date.now();
          } else if (Date.now() - silenceStart >= VAD_SILENCE_DURATION_MS) {
            stopRecording();
          }
        }
      }, VAD_CHECK_INTERVAL_MS);

      const maxTimer = setTimeout(() => {
        if (recordingRef.current && !speechRecognizer) {
          console.debug("[VoiceAssistant] Max recording duration reached");
          stopRecording();
        }
      }, VAD_MAX_RECORDING_MS);

      ownedSession = {
        mediaRecorder,
        speechRecognizer,
        stream,
        audioContext,
        analyser,
        vadInterval,
        maxTimer,
      };
      recordingRef.current = ownedSession;

      if (speechRecognizer && !speechRecognizer.start()) {
        if (recordingRef.current === ownedSession) {
          stopRecording({ process: false, releaseInput: true });
        }
        if (!hasTerminalRecognizerError) setErrorState();
        return;
      }

      playVoiceReadySuccessSound(soundEnabled);
      onReady();
    })
    .catch((err) => {
      if (requestId !== null && requestId !== micRequestIdRef.current) return;
      console.error("[VoiceAssistant] getUserMedia failed:", err);
      stopVoiceRecordingSession(
        {
          recordingRef,
          micReleaseRef,
          micStreamRef,
          micRequestIdRef,
        },
        { process: false, releaseInput: true },
      );
      if (isMissingAudioInputError(err)) {
        dispatchUserVoiceVolume(0, false);
        setRuntimeState("error");
        return;
      }
      setErrorState();
    });
}

function isTerminalSpeechRecognitionError(message: string): boolean {
  return /not-allowed|service-not-allowed|permission|audio-capture|network|language-not-supported|bad-grammar/i.test(
    message,
  );
}
